import React, {useState, useEffect} from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Divider,
    IconButton,
    useTheme,
    alpha,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    useMediaQuery,
    FormHelperText, InputLabel, Paper, Alert, Chip, FormControlLabel
} from "@mui/material";
import {CircularProgressbar, buildStyles} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import CryptoJS from "crypto-js";
import {KEY, ApiDetails} from "../Constants";
import { handle401Unauthorized, handle403Forbidden } from "../utils/authErrorHandler";
import { useNavigate } from "react-router-dom";
import { getUserData } from "../services/api/client";

// Icons
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PercentIcon from '@mui/icons-material/Percent';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import LoadingButton from '@mui/lab/LoadingButton';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import {People as PeopleIcon} from '@mui/icons-material';
// import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import AssessmentIcon from '@mui/icons-material/Assessment';
// import { showSnackbar } from "../../../../components/SnackBar/SnackBar";
import {showSnackbar} from "./SnackBar/SnackBar";
import {DataGrid} from "@mui/x-data-grid";
import {styled} from "@mui/system";
import WaterDropIcon from "@mui/icons-material/Water";
import {ErrorMessage, Field, Form, Formik} from "formik";
import CircularProgress from "@mui/material/CircularProgress";
import CardBillingConfirmationDialog from "../pages/tenant/tenant components/cardBillingConfirmatinDialog";
import {getMonthName} from "../pages/property/utils/helpers";
import CancelIcon from "@mui/icons-material/Cancel";
import DescriptionIcon from "@mui/icons-material/Description";
import {ReportLoadingBackdrop} from "../pages/tenantList/UserList";
import {BedIcon, GroupIcon, SettingsIcon} from "lucide-react";
import HomeIcon from "@mui/icons-material/Home";
import RefreshIcon from "@mui/icons-material/Refresh";

// Styled DataGrid for billing
const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    "& .MuiDataGrid-root": {
        height: "auto !important", // Auto height based on content
        minHeight: "100px", // Reduced minimum height
        maxHeight: "70vh", // Use viewport height for better responsiveness
        width: "100%",
    },
    "& .MuiDataGrid-main": {
        minHeight: "auto !important", // Remove default minHeight
    },
    "& .MuiDataGrid-virtualScroller": {
        minHeight: "auto !important", // Remove default minHeight
        height: "auto !important",
    },
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "0.9rem",
        fontWeight: 700,
        backgroundColor: "#E3F2FD", // Light blue background
        color: "#0D47A1", // Dark blue text
        minHeight: "48px !important",
        maxHeight: "48px !important",
    },
    "& .MuiDataGrid-row": {
        minHeight: "52px !important",
        maxHeight: "auto",
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#f5faf5", // Light green hover color
    },
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.85rem",
        padding: "8px",
    },
    "& .MuiDataGrid-footerContainer": {
        minHeight: "52px",
        borderTop: "1px solid #e0e0e0",
    },
}));

// Custom progress circle component
const ProgressCircle = ({value, text, label, description, icon, color, size = 180}) => {
    const theme = useTheme();
    const safeValue = isNaN(value) ? 0 : value;
    const formattedValue = safeValue.toFixed(1);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
            }
        }}>
            <Box sx={{position: 'relative', width: size, height: size}}>
                <CircularProgressbar
                    value={safeValue}
                    text={`${formattedValue}%`}
                    styles={buildStyles({
                        pathColor: color,
                        textColor: theme.palette.text.primary,
                        trailColor: alpha(color, 0.15),
                        textSize: '20px',
                        pathTransitionDuration: 1.5,
                    })}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -10,
                        backgroundColor: alpha(color, 0.1),
                        borderRadius: '50%',
                        p: 1,
                        color: color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 8px ${alpha(color, 0.2)}`
                    }}
                >
                    {icon}
                </Box>
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{mt: 2, textAlign: 'center'}}>
                {label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', mt: 0.5}}>
                {description}
            </Typography>
        </Box>
    );
};

// Deposit Management Component
const DepositManagement = ({open, onClose, onSave}) => {
    const theme = useTheme();
    const [depositItems, setDepositItems] = useState([{description: '', amount: '', refundable: ''}]);
    const [totalDeposit, setTotalDeposit] = useState(0);
    const [refundableDeposit, setRefundableDeposit] = useState(0);
    const [depositSet, setDepositSet] = useState(false);

    const addDepositItem = () => {
        setDepositItems([...depositItems, {description: '', amount: '', refundable: ''}]);
    };

    const updateDepositItem = (index, field, value) => {
        const updatedItems = [...depositItems];
        updatedItems[index][field] = value;
        setDepositItems(updatedItems);
        setDepositSet(false);
    };

    const removeDepositItem = (index) => {
        if (depositItems.length > 1) {
            const updatedItems = depositItems.filter((_, i) => i !== index);
            setDepositItems(updatedItems);
            setDepositSet(false);
        }
    };

    const calculateDeposit = () => {
        try {
            let total = 0;
            let refundable = 0;

            depositItems.forEach(item => {
                if (!item.description || !item.amount || !item.refundable) {
                    throw new Error("Please fill in all deposit fields");
                }

                if (item.refundable.toUpperCase() !== "YES" && item.refundable.toUpperCase() !== "NO") {
                    throw new Error("Refundable field must be YES or NO");
                }

                const amount = parseFloat(item.amount);
                total += amount;

                if (item.refundable.toUpperCase() === "YES") {
                    refundable += amount;
                }
            });

            setTotalDeposit(total);
            setRefundableDeposit(refundable);
            setDepositSet(true);
        } catch (error) {
            showSnackbar({
                title: "Error!",
                message: error.message,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    const handleSave = () => {
        if (!depositSet) {
            showSnackbar({
                title: "Error!",
                message: "Please calculate deposit first",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        onSave({
            items: depositItems,
            totalDeposit,
            refundableDeposit
        });
        onClose();
    };

    const resetForm = () => {
        setDepositItems([{description: '', amount: '', refundable: ''}]);
        setTotalDeposit(0);
        setRefundableDeposit(0);
        setDepositSet(false);
    };

    useEffect(() => {
        if (!open) {
            resetForm();
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{
                backgroundColor: "#2C5283",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
            }}>
                <Typography variant="h6">Add Deposit Details</Typography>
                <IconButton onClick={onClose} sx={{color: "white"}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{p: 3}}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3}}>
                    <Typography variant="h6" fontWeight="bold">Deposit Items</Typography>
                    <Button
                        onClick={addDepositItem}
                        variant="contained"
                        size="small"
                        sx={{
                            background: `linear-gradient(45deg, #2C5283, #1E3A63)`,
                            textTransform: "none"
                        }}
                    >
                        Add Item
                    </Button>
                </Box>

                {depositItems.map((item, index) => (
                    <Box key={index} sx={{
                        mb: 3,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: '#f9f9f9'
                    }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Typography variant="subtitle1" fontWeight="medium">
                                Item {index + 1}
                            </Typography>
                            {depositItems.length > 1 && (
                                <Button
                                    onClick={() => removeDepositItem(index)}
                                    size="small"
                                    color="error"
                                    sx={{textTransform: "none"}}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={item.description}
                                    onChange={(e) => updateDepositItem(index, 'description', e.target.value)}
                                    size="small"
                                    placeholder="e.g., House Deposit"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Amount (KSH)"
                                    type="number"
                                    value={item.amount}
                                    onChange={(e) => updateDepositItem(index, 'amount', e.target.value)}
                                    size="small"
                                    placeholder="0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={item.refundable}
                                        onChange={(e) => updateDepositItem(index, 'refundable', e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value="">Select Refundable</MenuItem>
                                        <MenuItem value="YES">YES - Refundable</MenuItem>
                                        <MenuItem value="NO">NO - Non-refundable</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                <Box sx={{mt: 3, mb: 2}}>
                    <Button
                        onClick={calculateDeposit}
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderColor: "#2C5283",
                            color: "#2C5283",
                            '&:hover': {
                                borderColor: "#1E3A63",
                                backgroundColor: alpha("#2C5283", 0.04)
                            }
                        }}
                    >
                        Calculate Deposit
                    </Button>
                </Box>

                {depositSet && (
                    <Box sx={{
                        p: 3,
                        backgroundColor: alpha("#4CAF50", 0.1),
                        borderRadius: 2,
                        border: "1px solid #4CAF50"
                    }}>
                        <Typography variant="h6" fontWeight="bold" sx={{mb: 2, color: "#2E7D32"}}>
                            Deposit Summary
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Total Deposit</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {totalDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Refundable Amount</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {refundableDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                Non-refundable: KSH {(totalDeposit - refundableDeposit).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{p: 3}}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!depositSet}
                    sx={{
                        background: `linear-gradient(45deg, #4CAF50, #2E7D32)`,
                        '&:disabled': {
                            backgroundColor: '#ccc'
                        }
                    }}
                >
                    Save Deposit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Add Tenant Dialog Component
const AddTenantDialog = ({open, onClose, selectedProperty}) => {
    const theme = useTheme();
    const matches = useMediaQuery("(max-width:700px)");
    const [loading, setLoading] = useState(false);
    const [depositModalOpen, setDepositModalOpen] = useState(false);
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState();
    const [waterReading, setWaterReading] = useState('');
    const [electricityReading, setElectricityReading] = useState('');
    const [depositDetails, setDepositDetails] = useState(null);
    const [depositAsArrears, setDepositAsArrears] = useState(true);
    const [rentAsArrears, setRentAsArrears] = useState(true);
    const [tenantPhoto] = useState(null);
    const [photoOfNationalID] = useState(null);
    const [copyOfContract] = useState(null);
    const [inventoryChecklist] = useState(null);

    // New state for Airbnb functionality
    const [isAirbnbUnit, setIsAirbnbUnit] = useState(false);
    const [bookingType, setBookingType] = useState('long_term'); // New field to track booking type
    const [checkInDate, setCheckInDate] = useState('');
    const [checkOutDate, setCheckOutDate] = useState('');
    const [guestCount, setGuestCount] = useState(1);
    const [specialRequests, setSpecialRequests] = useState('');

    const [formData, setFormData] = useState({
            first_name: '',
            last_name: '',
            phone_number: '',
            secondary_phone_number: '',
            national_id: '',
            rent: '',
            next_of_kin_first_name: '',
            next_of_kin_last_name: '',
            next_of_kin_relationship: '',
            next_of_kin_contacts: '',
            next_of_kin_national_id: '',
            occupation_or_profession: "",
            institution_name: "",
            place_of_work: "",
            reason_for_relocating: "",
            previous_residence: "",
            email_address: "",
        })
    ;

    // Safe decryption function
    const safeDecrypt = (key) => {
        try {
            return CryptoJS.AES.decrypt(localStorage.getItem(key), KEY).toString(CryptoJS.enc.Utf8) || "";
        } catch {
            return "";
        }
    };

    const originalText = safeDecrypt("myMap");

    useEffect(() => {
        setUnits([]);
        if (selectedProperty?.id) {
            fetchUnits(selectedProperty.id);
        }
    }, [selectedProperty]);

    const fetchUnits = (propertyId) => {
        try {
            if (!originalText) {
                console.error("User data not available");
                return;
            }

            const parsedData = JSON.parse(originalText);
            const token = parsedData?.[28]?.[1];

            if (!token) {
                console.error("Token not found in user data");
                return;
            }

            fetch(ApiDetails + "pegasus/visionary/property/getUnits", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": token
                },
                body: JSON.stringify({property_id: propertyId}),
            })
                .then(async (response) => {
                    let data = await response.json();
                    // Handle authentication/authorization errors
                if (response.status === 401) {
                        handle401Unauthorized();
                        return;
                    }
                    if (response.status === 403) {
                        handle403Forbidden();
                        return;
                    }
                    if (response.status === 200) {
                        // Handle both old format (array) and new format (object with units array)
                        const unitsData = Array.isArray(data) ? data : (data.units || []);
                        setUnits(unitsData);
                    }
                })
                .catch((err) => console.log(err));
        } catch (error) {
            console.error("Error parsing user data for fetchUnits:", error);
        }
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleUnitChange = (event) => {
        const unit = event.target.value;
        setSelectedUnit(unit);
        setWaterReading(unit.meter_reading);
        setElectricityReading(unit.electricity_meter_reading);

        // NEW: Check if this is an Airbnb unit
        const unitType = unit.unit_type || 'long_term';
        setIsAirbnbUnit(unitType === 'short_term');

        // Set default booking type based on unit type
        if (unitType === 'short_term') {
            setBookingType('short_term');
            // Pre-fill rent with nightly rate if available
            if (unit.nightly_rate) {
                setFormData(prev => ({
                    ...prev,
                    rent: unit.nightly_rate.toString()
                }));
            }
        } else {
            setBookingType('long_term');
        }
    };

    const handleDepositSave = (deposit) => {
        setDepositDetails(deposit);
        setDepositModalOpen(false);
    };

    // NEW: Helper function to get unit type chip
    const getUnitTypeChip = (unit) => {
        const unitType = unit.unit_type || 'long_term';
        if (unitType === 'short_term') {
            return (
                <Chip
                    size="small"
                    label="Airbnb"
                    color="error"
                    variant="outlined"
                    icon={<BedIcon fontSize="small"/>}
                    sx={{ml: 1, fontSize: '0.7rem'}}
                />
            );
        }
        return (
            <Chip
                size="small"
                label="Long Term"
                color="primary"
                variant="outlined"
                icon={<HomeIcon fontSize="small"/>}
                sx={{ml: 1, fontSize: '0.7rem'}}
            />
        );
    };

    // NEW: Calculate total cost for short-term bookings
    const calculateShortTermCost = () => {
        if (!checkInDate || !checkOutDate || !selectedUnit) return 0;

        const checkIn = new Date(checkInDate);
        const checkOut = new Date(checkOutDate);
        const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

        if (nights <= 0) return 0;

        const nightlyRate = selectedUnit.nightly_rate || 0;
        const cleaningFee = selectedUnit.cleaning_fee || 0;

        return (nights * nightlyRate) + cleaningFee;
    };

    const handleSubmit = () => {
        setLoading(true);

        // Existing validation
        if (!formData.first_name || !formData.last_name) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "First and last names are required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!formData.phone_number || formData.phone_number.length < 10) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Enter a valid phone number",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!selectedUnit) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please select a unit",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!formData.rent) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Rent amount is required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // NEW: Additional validation for short-term bookings
        if (isAirbnbUnit && bookingType === 'short_term') {
            if (!checkInDate || !checkOutDate) {
                setLoading(false);
                showSnackbar({
                    title: "Error!",
                    message: "Check-in and check-out dates are required for short-term bookings",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }

            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

            if (nights < (selectedUnit.minimum_stay_nights || 1)) {
                setLoading(false);
                showSnackbar({
                    title: "Error!",
                    message: `Minimum stay is ${selectedUnit.minimum_stay_nights || 1} nights`,
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }

            if (selectedUnit.maximum_stay_nights && nights > selectedUnit.maximum_stay_nights) {
                setLoading(false);
                showSnackbar({
                    title: "Error!",
                    message: `Maximum stay is ${selectedUnit.maximum_stay_nights} nights`,
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                return;
            }
        }

        // Deposit is only required for long-term rentals
        if ((!isAirbnbUnit || bookingType === 'long_term') && !depositDetails) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please add deposit details",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Validate email address before submission
        if (formData.email_address && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please enter a valid email address",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Prepare submission data (existing logic + new fields)
        const submitData = {
            ...formData,
            rent_as_arrear: rentAsArrears,
            deposit_as_arrears: depositAsArrears,
            is_student: "NO",
            email_address: formData.email_address || "",
            landlord_email_address: JSON.parse(originalText)[5][1],
            landlord_id: JSON.parse(originalText)[0][1],
            property_name: selectedProperty.property_name,
            property_id: selectedProperty.id,
            house_number: selectedUnit.unit,
            meter_reading: waterReading,
            electricity_meter_reading: electricityReading,

            // Handle deposit based on booking type
            ...(depositDetails ? {
                deposit: depositDetails.items.map(item => [item.description, item.amount, item.refundable]),
                deposit_held: depositDetails.refundableDeposit,
            } : {
                deposit: [],
                deposit_held: 0,
            }),

            // NEW: Add Airbnb-specific fields
            unit_type: selectedUnit.unit_type || 'long_term',
            booking_type: bookingType,
            airbnb_listing_id: selectedUnit.airbnb_listing_id || null,
            ...(isAirbnbUnit && bookingType === 'short_term' && {
                check_in_date: checkInDate,
                check_out_date: checkOutDate,
                guest_count: guestCount,
                special_requests: specialRequests,
                nightly_rate: selectedUnit.nightly_rate,
                cleaning_fee: selectedUnit.cleaning_fee,
                total_booking_cost: calculateShortTermCost()
            })
        };

        let formDataSubmit = new FormData();
        formDataSubmit.append("body", JSON.stringify(submitData));
        formDataSubmit.append("photo_of_national_id", photoOfNationalID);
        formDataSubmit.append("copy_of_contract", copyOfContract);
        formDataSubmit.append("inventory_checklist", inventoryChecklist);
        formDataSubmit.append("tenant_photo", tenantPhoto);

        fetch(ApiDetails + "pegasus/visionary/tenant/add/tenant", {
            method: "POST",
            headers: {
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: formDataSubmit,
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    const successMessage = isAirbnbUnit && bookingType === 'short_term'
                        ? "Guest booking created successfully!"
                        : "Tenant added successfully.";

                    showSnackbar({
                        title: "Success!",
                        message: successMessage,
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    onClose();
                    window.location.reload();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong. Please try again later",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'visible'
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`,
                    position: 'relative'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    p: 1,
                                    borderRadius: 2,
                                    mr: 2,
                                    color: theme.palette.primary.main
                                }}
                            >
                                <PersonAddIcon/>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    {/* NEW: Dynamic title based on unit type */}
                                    {isAirbnbUnit && bookingType === 'short_term' ? 'Add New Guest' : 'Add New Tenant'}
                                </Typography>
                                {selectedProperty && (
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedProperty.property_name} • {selectedProperty.location}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{pt: 3, zoom: matches ? "85%" : "100%"}}>
                    <Grid container spacing={3}>
                        {/* Personal Details */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{mb: 2}}>
                                {isAirbnbUnit && bookingType === 'short_term' ? 'Guest Details' : 'Personal Details'}
                            </Typography>
                            <Divider sx={{mb: 2}}/>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={formData.first_name}
                                onChange={handleInputChange('first_name')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={formData.last_name}
                                onChange={handleInputChange('last_name')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={formData.email_address}
                                onChange={handleInputChange('email_address')}
                                InputProps={{
                                    startAdornment: <EmailIcon sx={{color: "#2C5283", mr: 1}}/>
                                }}
                                error={formData.email_address && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)}
                                helperText={formData.email_address && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address) ? "Please enter a valid email address" : ""}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                value={formData.phone_number}
                                onChange={handleInputChange('phone_number')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Secondary Phone"
                                value={formData.secondary_phone_number}
                                onChange={handleInputChange('secondary_phone_number')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="National ID"
                                value={formData.national_id}
                                onChange={handleInputChange('national_id')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={isAirbnbUnit && bookingType === 'short_term' ? 'Nightly Rate (KSH)' : 'Monthly Rent (KSH)'}
                                type="number"
                                value={formData.rent}
                                onChange={handleInputChange('rent')}
                                required
                                InputProps={{
                                    startAdornment: isAirbnbUnit && bookingType === 'short_term' ? (
                                        <NightsStayIcon sx={{color: "#FF5722", mr: 1}}/>
                                    ) : (
                                        <AttachMoneyIcon sx={{color: "#4CAF50", mr: 1}}/>
                                    )
                                }}
                            />
                        </Grid>

                        {/* NEW: Short-term booking specific fields */}
                        {isAirbnbUnit && bookingType === 'short_term' && (
                            <>
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{mt: 1}}>
                                        <Typography variant="body2">
                                            <strong>Airbnb Unit Selected:</strong> Additional booking details required
                                            for short-term rental.
                                        </Typography>
                                    </Alert>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Check-in Date"
                                        type="date"
                                        value={checkInDate}
                                        onChange={(e) => setCheckInDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            min: new Date().toISOString().split('T')[0]
                                        }}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Check-out Date"
                                        type="date"
                                        value={checkOutDate}
                                        onChange={(e) => setCheckOutDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            min: checkInDate || new Date().toISOString().split('T')[0]
                                        }}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Number of Guests"
                                        type="number"
                                        value={guestCount}
                                        onChange={(e) => setGuestCount(Math.max(1, parseInt(e.target.value) || 1))}
                                        inputProps={{min: 1, max: 10}}
                                        InputProps={{
                                            startAdornment: <GroupIcon sx={{color: "#2196F3", mr: 1}}/>
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Special Requests"
                                        value={specialRequests}
                                        onChange={(e) => setSpecialRequests(e.target.value)}
                                        placeholder="e.g., Early check-in, extra towels"
                                    />
                                </Grid>

                                {/* NEW: Booking Summary */}
                                {checkInDate && checkOutDate && selectedUnit && (
                                    <Grid item xs={12}>
                                        <Paper sx={{p: 2, backgroundColor: alpha(theme.palette.success.main, 0.1)}}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Booking Summary
                                            </Typography>
                                            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                                                <Typography variant="body2">
                                                    {Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24))} nights
                                                    × KSH {selectedUnit.nightly_rate?.toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2">
                                                    KSH {(Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24)) * (selectedUnit.nightly_rate || 0)).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            {selectedUnit.cleaning_fee && (
                                                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                                                    <Typography variant="body2">Cleaning fee</Typography>
                                                    <Typography
                                                        variant="body2">KSH {selectedUnit.cleaning_fee.toLocaleString()}</Typography>
                                                </Box>
                                            )}
                                            <Divider sx={{my: 1}}/>
                                            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Typography variant="subtitle2">Total</Typography>
                                                <Typography variant="subtitle2">
                                                    KSH {calculateShortTermCost().toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* Property Details */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{mb: 2, mt: 2}}>Property Details</Typography>
                            <Divider sx={{mb: 2}}/>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <Select
                                    value={selectedUnit}
                                    onChange={handleUnitChange}
                                    displayEmpty
                                    disabled={!units || units.length === 0}
                                >
                                    <MenuItem value="" disabled>Select Unit</MenuItem>
                                    {units && units.length > 0 ? (
                                        units.map((unit, index) => (
                                            <MenuItem
                                                key={index}
                                                value={unit}
                                                disabled={unit.state === "OCCUPIED"}
                                            >
                                                <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
                                                    <Typography>{unit.unit}</Typography>
                                                    {getUnitTypeChip(unit)}
                                                    <Box component="span" sx={{
                                                        ml: 'auto',
                                                        color: unit.state === "OCCUPIED" ? "error.main" : "success.main",
                                                        fontSize: "0.8rem"
                                                    }}>
                                                        ({unit.state.toLowerCase()})
                                                    </Box>
                                                </Box>
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>No units available</MenuItem>
                                    )}
                                </Select>
                                <FormHelperText>
                                    {!units || units.length === 0 ? "No available units" : "Required"}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        {/* NEW: Booking Type Selection for Airbnb units */}
                        {isAirbnbUnit && (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Booking Type</InputLabel>
                                    <Select
                                        value={bookingType}
                                        onChange={(e) => setBookingType(e.target.value)}
                                        label="Booking Type"
                                    >
                                        <MenuItem value="short_term">
                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                <BedIcon sx={{mr: 1, color: '#FF5722'}}/>
                                                Short-term (Airbnb Guest)
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="long_term">
                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                <HomeIcon sx={{mr: 1, color: '#2196F3'}}/>
                                                Long-term (Traditional Tenant)
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        Choose how this unit will be rented
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Deposit section - Only for long-term rentals */}
                        {(!isAirbnbUnit || bookingType === 'long_term') && (
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setDepositModalOpen(true)}
                                    sx={{
                                        height: 56,
                                        textTransform: "none",
                                        backgroundColor: depositDetails ? alpha(theme.palette.success.main, 0.1) : "#FBF4F0",
                                        borderColor: depositDetails ? theme.palette.success.main : theme.palette.divider,
                                        color: depositDetails ? theme.palette.success.main : theme.palette.text.primary
                                    }}
                                >
                                    {depositDetails ? `Deposit Set: KSH ${depositDetails.totalDeposit.toLocaleString()}` : "Add Deposit Details"}
                                </Button>
                            </Grid>
                        )}

                        {/* Show deposit not required notice for Airbnb */}
                        {isAirbnbUnit && bookingType === 'short_term' && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        <strong>No Deposit Required:</strong>
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Water Meter Reading"
                                type="number"
                                value={waterReading}
                                onChange={(e) => setWaterReading(e.target.value)}
                                disabled={!selectedUnit}
                                InputProps={{
                                    startAdornment: <WaterDropIcon sx={{color: "#2196F3", mr: 1}}/>
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Electricity Meter Reading"
                                type="number"
                                value={electricityReading}
                                onChange={(e) => setElectricityReading(e.target.value)}
                                disabled={!selectedUnit}
                                InputProps={{
                                    startAdornment: <ElectricBoltIcon sx={{color: "#FF9800", mr: 1}}/>
                                }}
                            />
                        </Grid>

                        {/* Billing Options - Only show for long-term bookings */}
                        {(!isAirbnbUnit || bookingType === 'long_term') && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                        <Typography>Add Deposit As Arrears</Typography>
                                        <Switch
                                            checked={depositAsArrears}
                                            onChange={(e) => setDepositAsArrears(e.target.checked)}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                        <Typography>Add Rent As Arrears</Typography>
                                        <Switch
                                            checked={rentAsArrears}
                                            onChange={(e) => setRentAsArrears(e.target.checked)}
                                        />
                                    </Box>
                                </Grid>
                            </>
                        )}

                        {/* Next of Kin Details - Always show but less prominent for short-term */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{mb: 2, mt: 2}}>
                                {isAirbnbUnit && bookingType === 'short_term' ? 'Emergency Contact (Optional)' : 'Next of Kin Details'}
                            </Typography>
                            <Divider sx={{mb: 2}}/>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin First Name"
                                value={formData.next_of_kin_first_name}
                                onChange={handleInputChange('next_of_kin_first_name')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin Last Name"
                                value={formData.next_of_kin_last_name}
                                onChange={handleInputChange('next_of_kin_last_name')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Relationship"
                                value={formData.next_of_kin_relationship}
                                onChange={handleInputChange('next_of_kin_relationship')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin Contacts"
                                value={formData.next_of_kin_contacts}
                                onChange={handleInputChange('next_of_kin_contacts')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin National ID"
                                value={formData.next_of_kin_national_id}
                                onChange={handleInputChange('next_of_kin_national_id')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{p: 3, pt: 1}}>
                    <Button onClick={onClose} color="inherit">
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleSubmit}
                        loading={loading}
                        variant="contained"
                        startIcon={isAirbnbUnit && bookingType === 'short_term' ? <BedIcon/> : <PersonAddIcon/>}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                        }}
                    >
                        {isAirbnbUnit && bookingType === 'short_term' ? 'Create Booking' : 'Add Tenant'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <DepositManagement
                open={depositModalOpen}
                onClose={() => setDepositModalOpen(false)}
                onSave={handleDepositSave}
            />
        </>
    );
};

//CORRECT AND UPDATED
// Bill Tenants Dialog Component
const BillTenantsDialog = ({open, onClose, selectedProperty}) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [fetchingTenants, setFetchingTenants] = useState(false);
    const [month, setMonth] = useState(new Date());
    const [tenants, setTenants] = useState([]);
    const [billedTenants, setBilledTenants] = useState([]);
    const [elecBilledTenants, setElecBilledTenants] = useState([]);
    const [htmlContent, setHtmlContent] = useState('');
    const [showBillingConfirmDialog, setShowBillingConfirmDialog] = useState(false);
    const [billingDataForConfirm, setBillingDataForConfirm] = useState(null);

    // New state for utility toggles
    const [tenantUtilitySettings, setTenantUtilitySettings] = useState({});
    // New state for billing inclusion
    const [tenantBillingStatus, setTenantBillingStatus] = useState({});

    // Safe decryption function
    const safeDecrypt = (key) => {
        try {
            return CryptoJS.AES.decrypt(localStorage.getItem(key), KEY).toString(CryptoJS.enc.Utf8) || "";
        } catch {
            return "";
        }
    };

    const originalText = safeDecrypt("myMap");

    useEffect(() => {
        if (selectedProperty?.id && open) {
            fetchTenants();
        }
    }, [selectedProperty, open]);

    const fetchTenants = () => {
        setFetchingTenants(true);
        setTenants([]);
        setTenantUtilitySettings({});
        setTenantBillingStatus({});

        const userData = getUserData();
        const token = userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;

        fetch(ApiDetails + "pegasus/visionary/tenant/get/property/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": token || ""
            },
            body: JSON.stringify({property_id: selectedProperty.id}),
        })
            .then(async (response) => {
                let data = await response.json();
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    setTenants(data);
                    // Initialize utility settings and billing status for each tenant
                    const initialSettings = {};
                    const initialBillingStatus = {};
                    data.forEach(tenant => {
                        initialSettings[tenant.tenant_id] = {
                            hasWater: true,
                            hasElectricity: true
                        };
                        initialBillingStatus[tenant.tenant_id] = true; // Include in billing by default
                    });
                    setTenantUtilitySettings(initialSettings);
                    setTenantBillingStatus(initialBillingStatus);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to fetch tenants",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while fetching tenants",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            })
            .finally(() => {
                setFetchingTenants(false);
            });
    };

// UPDATED: Handle billing for tenants who don't use water
    const handleWaterBillingForNonUser = (tenantId, tenant) => {
        setBilledTenants(prev => {
            // Remove existing entry for this tenant
            const filteredTenantArray = prev.filter((t) => t.tenant_id !== tenantId);

            // Apply minimum water bill or 0
            const finalWaterBill = selectedProperty.minimum_water_bill || 0;

            // Use Wallet as arrears only if status is PENDING
            const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
            return [...filteredTenantArray, {
                tenant_id: tenantId,
                rent: parseInt(tenant.rent.toString().replace(",", "")),
                arrears: arrears,
                status: tenant.status,
                finalWaterBill: parseFloat(finalWaterBill),
                total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(finalWaterBill),
                meter_reading: tenant.meter_reading,
                landlord_id: JSON.parse(originalText)[0][1],
                isMinimumCharge: true
            }];
        });
    };

// UPDATED: Handle billing for tenants who don't use electricity
    const handleElectricityBillingForNonUser = (tenantId, tenant) => {
        setElecBilledTenants(prev => {
            // Remove existing entry for this tenant
            const filteredTenantArray = prev.filter((t) => t.tenant_id !== tenantId);

            // Apply minimum electricity bill or 0
            const finalElecBill = selectedProperty.minimum_electricity_bill || 0;

            // Use Wallet as arrears only if status is PENDING
            const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
            return [...filteredTenantArray, {
                tenant_id: tenantId,
                rent: parseInt(tenant.rent.toString().replace(",", "")),
                arrears: arrears,
                status: tenant.status,
                electricity_bill: parseFloat(finalElecBill),
                total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(finalElecBill),
                electricity_meter_reading: tenant.electricity_meter_reading,
                landlord_id: JSON.parse(originalText)[0][1],
                isMinimumCharge: true
            }];
        });
    };

    const getRowStyle = (params) => {
        const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
        const isEnabled = params.row.billing_enabled && isIncludedInBilling;

        return {
            backgroundColor: isEnabled ? "transparent" : "#f0f0f0",
            color: isEnabled ? "inherit" : "#888",
            pointerEvents: isEnabled ? "auto" : "none",
            opacity: isEnabled ? 1 : 0.6,
        };
    };

    const handleWaterReadingChange = (tenantId, value, tenant) => {
        if (!value) return;

        let filteredTenantArray = billedTenants.filter((t) => t.tenant_id !== tenantId);

        let consumedUnits = value - tenant.meter_reading;
        consumedUnits = consumedUnits.toFixed(1);

        let finalWaterBill = parseFloat(consumedUnits) * parseInt(selectedProperty.water_bill);

        if (finalWaterBill < selectedProperty.minimum_water_bill) {
            finalWaterBill = selectedProperty.minimum_water_bill;
        }

        // Use Wallet as arrears only if status is PENDING
        const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
        setBilledTenants([...filteredTenantArray, {
            tenant_id: tenantId,
            rent: parseInt(tenant.rent.toString().replace(",", "")),
            arrears: arrears,
            status: tenant.status,
            finalWaterBill: parseFloat(finalWaterBill),
            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(finalWaterBill),
            meter_reading: value,
            landlord_id:  JSON.parse(originalText)[0][1],
        }]);
    };

    const handleElectricityReadingChange = (tenantId, value, tenant) => {
        if (!value) return;

        let filteredTenantArray = elecBilledTenants.filter((t) => t.tenant_id !== tenantId);

        let consumedUnits = value - tenant.electricity_meter_reading;
        consumedUnits = consumedUnits.toFixed(1);

        let finalElecBill = parseFloat(consumedUnits) * parseInt(selectedProperty.electricity_bill);

        if (finalElecBill < selectedProperty.minimum_electricity_bill) {
            finalElecBill = selectedProperty.minimum_electricity_bill;
        }

        // Use Wallet as arrears only if status is PENDING
        const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
        setElecBilledTenants([...filteredTenantArray, {
            tenant_id: tenantId,
            rent: parseInt(tenant.rent.toString().replace(",", "")),
            arrears: arrears,
            status: tenant.status,
            electricity_bill: parseFloat(finalElecBill),
            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(finalElecBill),
            electricity_meter_reading: value,
            landlord_id: JSON.parse(originalText)[0][1],
        }]);
    };

    const billingColumns = [
        {
            field: "billing_status",
            headerName: "Include",
            flex: 0.5,
            minWidth: 80,
            renderCell: (params) => (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <Switch
                        size="small"
                        checked={tenantBillingStatus[params.row.tenant_id] || false}
                        onChange={(e) => handleBillingStatusToggle(params.row.tenant_id, e.target.checked)}
                        disabled={!params.row.billing_enabled}
                        color="primary"
                    />
                </Box>
            ),
        },
        {
            field: "user",
            headerName: "Name",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={getRowStyle(params)}>
                    {params.row.first_name} {params.row.last_name}
                </div>
            ),
        },
        {
            field: "house_number",
            headerName: "Unit",
            flex: 0.8,
            minWidth: 80,
            renderCell: (params) => (
                <div style={getRowStyle(params)}>{params.row.house_number}</div>
            ),
        },
        {
            field: "rent",
            headerName: "Rent",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <div style={{...getRowStyle(params), color: "green", fontWeight: 600}}>
                    KSH {parseInt(params.row.rent.toString().replace(",", "")).toLocaleString()}
                </div>
            ),
        },
        {
            field: "utilities_toggle",
            headerName: "Utilities",
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
                const isEnabled = params.row.billing_enabled && isIncludedInBilling;

                return (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 0.3, ...getRowStyle(params)}}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={settings.hasWater || false}
                                    onChange={(e) => handleUtilityToggle(params.row.tenant_id, 'hasWater', e.target.checked)}
                                    disabled={!isEnabled}
                                />
                            }
                            label={
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                    <WaterDropIcon sx={{color: "#2196F3", fontSize: 14}}/>
                                    <Typography variant="caption" sx={{fontSize: '0.7rem'}}>Water</Typography>
                                </Box>
                            }
                            sx={{margin: 0, height: 20}}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={settings.hasElectricity || false}
                                    onChange={(e) => handleUtilityToggle(params.row.tenant_id, 'hasElectricity', e.target.checked)}
                                    disabled={!isEnabled}
                                />
                            }
                            label={
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                    <ElectricBoltIcon sx={{color: "#FF9800", fontSize: 14}}/>
                                    <Typography variant="caption" sx={{fontSize: '0.7rem'}}>Electricity</Typography>
                                </Box>
                            }
                            sx={{margin: 0, height: 20}}
                        />
                    </Box>
                );
            },
        },
        {
            field: "meter_reading",
            headerName: "Water Reading",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                return (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <WaterDropIcon
                            fontSize="small"
                            sx={{
                                color: settings.hasWater ? "#2196F3" : "#ccc"
                            }}
                        />
                        <span style={{color: settings.hasWater ? 'inherit' : '#ccc'}}>
                            {params.row.meter_reading}
                        </span>
                        {!settings.hasWater && (
                            <Chip
                                label="Min"
                                size="small"
                                variant="outlined"
                                sx={{fontSize: '0.6rem', height: 16}}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: "current_meter_reading",
            headerName: "New Water Reading",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
                const isWaterEnabled = settings.hasWater && isIncludedInBilling;
                const isEnabled = params.row.billing_enabled && isIncludedInBilling;

                return isWaterEnabled ? (
                    <TextField
                        type="number"
                        placeholder="Enter Reading..."
                        onChange={(e) => handleWaterReadingChange(params.row.tenant_id, e.target.value, params.row)}
                        disabled={!isEnabled}
                        InputProps={{
                            startAdornment: <WaterDropIcon sx={{color: "#2196F3", mr: 1}}/>
                        }}
                        variant="outlined"
                        size="small"
                        sx={{backgroundColor: "white", ...getRowStyle(params)}}
                    />
                ) : (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.8rem'}}>
                            KSH {selectedProperty?.minimum_water_bill || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                            (min)
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "electricity_meter_reading",
            headerName: "Electricity Reading",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                return (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <ElectricBoltIcon
                            fontSize="small"
                            sx={{
                                color: settings.hasElectricity ? "#FF9800" : "#ccc"
                            }}
                        />
                        <span style={{color: settings.hasElectricity ? 'inherit' : '#ccc'}}>
                            {params.row.electricity_meter_reading}
                        </span>
                        {!settings.hasElectricity && (
                            <Chip
                                label="Min"
                                size="small"
                                variant="outlined"
                                sx={{fontSize: '0.6rem', height: 16}}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: "current_electricity_meter_reading",
            headerName: "New Electricity Reading",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
                const isElectricityEnabled = settings.hasElectricity && isIncludedInBilling;
                const isEnabled = params.row.billing_enabled && isIncludedInBilling;

                return isElectricityEnabled ? (
                    <TextField
                        type="number"
                        placeholder="Enter Reading..."
                        onChange={(e) => handleElectricityReadingChange(params.row.tenant_id, e.target.value, params.row)}
                        disabled={!isEnabled}
                        InputProps={{
                            startAdornment: <ElectricBoltIcon sx={{color: "#FF9800", mr: 1}}/>
                        }}
                        variant="outlined"
                        size="small"
                        sx={{backgroundColor: "white", ...getRowStyle(params)}}
                    />
                ) : (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.8rem'}}>
                            KSH {selectedProperty?.minimum_electricity_bill || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                            (min)
                        </Typography>
                    </Box>
                );
            },
        },
    ];

// CORRECTED: Improved bulk utility toggle
    const handleBulkUtilityToggle = (utilityType, enabled) => {
        const updatedSettings = {...tenantUtilitySettings};

        // Get only included tenants that are billing enabled
        const eligibleTenants = tenants.filter(tenant =>
            tenant.billing_enabled && tenantBillingStatus[tenant.tenant_id]
        );

        // Update all utility settings first
        eligibleTenants.forEach(tenant => {
            updatedSettings[tenant.tenant_id] = {
                ...updatedSettings[tenant.tenant_id],
                [utilityType]: enabled
            };
        });

        // Apply the updated settings
        setTenantUtilitySettings(updatedSettings);

        if (!enabled) {
            // Apply minimum charges for disabled utilities - using functional updates
            if (utilityType === 'hasWater') {
                setBilledTenants(prevBilled => {
                    // Remove existing entries for these tenants first
                    let updatedBilled = prevBilled.filter(existing =>
                        !eligibleTenants.some(tenant => tenant.tenant_id === existing.tenant_id)
                    );

                    // Add minimum charges for all eligible tenants
                    const newBilledEntries = eligibleTenants.map(tenant => {
                        // Use Wallet as arrears only if status is PENDING
                        const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
                        return {
                            tenant_id: tenant.tenant_id,
                            rent: parseInt(tenant.rent.toString().replace(",", "")),
                            arrears: arrears,
                            status: tenant.status,
                            finalWaterBill: parseFloat(selectedProperty.minimum_water_bill || 0),
                            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(selectedProperty.minimum_water_bill || 0),
                            meter_reading: tenant.meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                            isMinimumCharge: true
                        };
                    });

                    return [...updatedBilled, ...newBilledEntries];
                });
            } else if (utilityType === 'hasElectricity') {
                setElecBilledTenants(prevBilled => {
                    // Remove existing entries for these tenants first
                    let updatedBilled = prevBilled.filter(existing =>
                        !eligibleTenants.some(tenant => tenant.tenant_id === existing.tenant_id)
                    );

                    // Add minimum charges for all eligible tenants
                    const newBilledEntries = eligibleTenants.map(tenant => {
                        // Use Wallet as arrears only if status is PENDING
                        const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
                        return {
                            tenant_id: tenant.tenant_id,
                            rent: parseInt(tenant.rent.toString().replace(",", "")),
                            arrears: arrears,
                            status: tenant.status,
                            electricity_bill: parseFloat(selectedProperty.minimum_electricity_bill || 0),
                            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(selectedProperty.minimum_electricity_bill || 0),
                            electricity_meter_reading: tenant.electricity_meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                            isMinimumCharge: true
                        };
                    });

                    return [...updatedBilled, ...newBilledEntries];
                });
            }
        } else {
            // Remove from billing arrays if re-enabled to allow manual input
            if (utilityType === 'hasWater') {
                setBilledTenants(prev => prev.filter(t =>
                    !eligibleTenants.some(tenant => tenant.tenant_id === t.tenant_id)
                ));
            } else if (utilityType === 'hasElectricity') {
                setElecBilledTenants(prev => prev.filter(t =>
                    !eligibleTenants.some(tenant => tenant.tenant_id === t.tenant_id)
                ));
            }
        }
    };

// CORRECTED: Individual utility toggle function (also needs fixing)
    const handleUtilityToggle = (tenantId, utilityType, enabled) => {
        setTenantUtilitySettings(prev => ({
            ...prev,
            [tenantId]: {
                ...prev[tenantId],
                [utilityType]: enabled
            }
        }));

        // Only apply billing changes if tenant is included in billing
        const isIncludedInBilling = tenantBillingStatus[tenantId];
        if (!isIncludedInBilling) return;

        if (!enabled) {
            // If utility is disabled, apply minimum charge using functional update
            const tenant = tenants.find(t => t.tenant_id === tenantId);
            if (tenant) {
                // Use Wallet as arrears only if status is PENDING
                const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
                if (utilityType === 'hasWater') {
                    setBilledTenants(prev => {
                        const filtered = prev.filter(t => t.tenant_id !== tenantId);
                        return [...filtered, {
                            tenant_id: tenantId,
                            rent: parseInt(tenant.rent.toString().replace(",", "")),
                            arrears: arrears,
                            status: tenant.status,
                            finalWaterBill: parseFloat(selectedProperty.minimum_water_bill || 0),
                            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(selectedProperty.minimum_water_bill || 0),
                            meter_reading: tenant.meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                            isMinimumCharge: true
                        }];
                    });
                } else if (utilityType === 'hasElectricity') {
                    setElecBilledTenants(prev => {
                        const filtered = prev.filter(t => t.tenant_id !== tenantId);
                        return [...filtered, {
                            tenant_id: tenantId,
                            rent: parseInt(tenant.rent.toString().replace(",", "")),
                            arrears: arrears,
                            status: tenant.status,
                            electricity_bill: parseFloat(selectedProperty.minimum_electricity_bill || 0),
                            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(selectedProperty.minimum_electricity_bill || 0),
                            electricity_meter_reading: tenant.electricity_meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                            isMinimumCharge: true
                        }];
                    });
                }
            }
        } else {
            // If re-enabled, remove from billed arrays to allow manual input
            if (utilityType === 'hasWater') {
                setBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
            } else if (utilityType === 'hasElectricity') {
                setElecBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
            }
        }
    };

// UPDATED: Enhanced billing status toggle
    const handleBillingStatusToggle = (tenantId, included) => {
        setTenantBillingStatus(prev => ({
            ...prev,
            [tenantId]: included
        }));

        if (!included) {
            // Remove from billing arrays if excluded
            setBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
            setElecBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
        } else {
            // If including back, check if utilities are disabled and apply minimum charges
            const tenant = tenants.find(t => t.tenant_id === tenantId);
            const settings = tenantUtilitySettings[tenantId] || {};

            if (tenant) {
                // Use Wallet as arrears only if status is PENDING
                const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;

                // Auto-apply minimum charges for disabled utilities when including tenant
                if (!settings.hasWater) {
                    setBilledTenants(prev => {
                        // Check if not already billed
                        if (!prev.find(t => t.tenant_id === tenantId)) {
                            const finalWaterBill = selectedProperty.minimum_water_bill || 0;
                            return [...prev, {
                                tenant_id: tenantId,
                                rent: parseInt(tenant.rent.toString().replace(",", "")),
                                arrears: arrears,
                                status: tenant.status,
                                finalWaterBill: parseFloat(finalWaterBill),
                                total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(finalWaterBill),
                                meter_reading: tenant.meter_reading,
                                landlord_id: JSON.parse(originalText)[0][1],
                                isMinimumCharge: true
                            }];
                        }
                        return prev;
                    });
                }

                if (!settings.hasElectricity) {
                    setElecBilledTenants(prev => {
                        // Check if not already billed
                        if (!prev.find(t => t.tenant_id === tenantId)) {
                            const finalElecBill = selectedProperty.minimum_electricity_bill || 0;
                            return [...prev, {
                                tenant_id: tenantId,
                                rent: parseInt(tenant.rent.toString().replace(",", "")),
                                arrears: arrears,
                                status: tenant.status,
                                electricity_bill: parseFloat(finalElecBill),
                                total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(finalElecBill),
                                electricity_meter_reading: tenant.electricity_meter_reading,
                                landlord_id: JSON.parse(originalText)[0][1],
                                isMinimumCharge: true
                            }];
                        }
                        return prev;
                    });
                }
            }
        }
    };

// UPDATED: Improved bulk billing toggle
    const handleBulkBillingToggle = (included) => {
        const updatedStatus = {};

        tenants.forEach(tenant => {
            if (tenant.billing_enabled) {
                updatedStatus[tenant.tenant_id] = included;
            }
        });

        setTenantBillingStatus(prev => ({...prev, ...updatedStatus}));

        if (!included) {
            // Remove ALL tenants from billing arrays if excluded
            setBilledTenants(prev => prev.filter(t =>
                !tenants.some(tenant => tenant.billing_enabled && tenant.tenant_id === t.tenant_id)
            ));
            setElecBilledTenants(prev => prev.filter(t =>
                !tenants.some(tenant => tenant.billing_enabled && tenant.tenant_id === t.tenant_id)
            ));
        } else {
            // When including all, apply minimum charges for tenants with disabled utilities
            const eligibleTenants = tenants.filter(tenant => tenant.billing_enabled);

            // Handle water billing for tenants with disabled water
            setBilledTenants(prev => {
                const tenantsNeedingWaterMinimum = eligibleTenants.filter(tenant => {
                    const settings = tenantUtilitySettings[tenant.tenant_id] || {};
                    return !settings.hasWater && !prev.find(t => t.tenant_id === tenant.tenant_id);
                });

                const newWaterBills = tenantsNeedingWaterMinimum.map(tenant => {
                    // Use Wallet as arrears only if status is PENDING
                    const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
                    return {
                        tenant_id: tenant.tenant_id,
                        rent: parseInt(tenant.rent.toString().replace(",", "")),
                        arrears: arrears,
                        status: tenant.status,
                        finalWaterBill: parseFloat(selectedProperty.minimum_water_bill || 0),
                        total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(selectedProperty.minimum_water_bill || 0),
                        meter_reading: tenant.meter_reading,
                        landlord_id: JSON.parse(originalText)[0][1],
                        isMinimumCharge: true
                    };
                });

                return [...prev, ...newWaterBills];
            });

            // Handle electricity billing for tenants with disabled electricity
            setElecBilledTenants(prev => {
                const tenantsNeedingElectricityMinimum = eligibleTenants.filter(tenant => {
                    const settings = tenantUtilitySettings[tenant.tenant_id] || {};
                    return !settings.hasElectricity && !prev.find(t => t.tenant_id === tenant.tenant_id);
                });

                const newElectricityBills = tenantsNeedingElectricityMinimum.map(tenant => {
                    // Use Wallet as arrears only if status is PENDING
                    const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
                    return {
                        tenant_id: tenant.tenant_id,
                        rent: parseInt(tenant.rent.toString().replace(",", "")),
                        arrears: arrears,
                        status: tenant.status,
                        electricity_bill: parseFloat(selectedProperty.minimum_electricity_bill || 0),
                        total_bill: parseInt(tenant.rent.toString().replace(",", "")) + arrears + parseFloat(selectedProperty.minimum_electricity_bill || 0),
                        electricity_meter_reading: tenant.electricity_meter_reading,
                        landlord_id: JSON.parse(originalText)[0][1],
                        isMinimumCharge: true
                    };
                });

                return [...prev, ...newElectricityBills];
            });
        }
    };

    const handleBillTenants = async () => {
        setLoading(true);

        // Get only included tenants who are billing enabled AND have Include switch ON
        const includedTenants = tenants.filter(tenant => {
            const isBillingEnabled = tenant.billing_enabled === true;
            const isIncludedInBilling = tenantBillingStatus[tenant.tenant_id] === true;
            // Only include if BOTH conditions are true
            return isBillingEnabled && isIncludedInBilling;
        });

        if (includedTenants.length === 0) {
            showSnackbar({
                title: "Error!",
                message: "No tenants are included in billing.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
            return;
        }

        // Step 1: Validate that tenants with enabled utilities have provided readings
        const validationErrors = [];

        for (const tenant of includedTenants) {
            const settings = tenantUtilitySettings[tenant.tenant_id] || {};

            // Check if water is enabled but no reading provided
            if (settings.hasWater) {
                const hasWaterReading = billedTenants.find(t => t.tenant_id === tenant.tenant_id);
                if (!hasWaterReading) {
                    validationErrors.push(`${tenant.first_name} ${tenant.last_name} (Unit ${tenant.house_number}): Water reading required`);
                }
            }

            // Check if electricity is enabled but no reading provided
            if (settings.hasElectricity) {
                const hasElectricityReading = elecBilledTenants.find(t => t.tenant_id === tenant.tenant_id);
                if (!hasElectricityReading) {
                    validationErrors.push(`${tenant.first_name} ${tenant.last_name} (Unit ${tenant.house_number}): Electricity reading required`);
                }
            }
        }

        // If there are validation errors, show them and stop
        if (validationErrors.length > 0) {
            showSnackbar({
                title: "Missing Readings!",
                message: `Please provide readings for:\n${validationErrors.slice(0, 3).join('\n')}${validationErrors.length > 3 ? '\n...and more' : ''}`,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
            return;
        }

        // Step 2: Auto-apply minimum charges for included tenants with disabled utilities
        for (const tenant of includedTenants) {
            const settings = tenantUtilitySettings[tenant.tenant_id] || {};

            // Auto-bill for water if disabled and not already billed
            if (!settings.hasWater && !billedTenants.find(t => t.tenant_id === tenant.tenant_id)) {
                handleWaterBillingForNonUser(tenant.tenant_id, tenant);
            }

            // Auto-bill for electricity if disabled and not already billed
            if (!settings.hasElectricity && !elecBilledTenants.find(t => t.tenant_id === tenant.tenant_id)) {
                handleElectricityBillingForNonUser(tenant.tenant_id, tenant);
            }
        }

        // Step 3: Compile final bills for included tenants only
        const finalBill = [];

        for (const tenant of includedTenants) {
            const waterBill = billedTenants.find(t => t.tenant_id === tenant.tenant_id);
            const electricityBill = elecBilledTenants.find(t => t.tenant_id === tenant.tenant_id);

            // Create bill entry for this tenant
            const billEntry = {
                tenant_id: tenant.tenant_id,
                water_bill: waterBill ? waterBill.finalWaterBill : 0,
                electricity_bill: electricityBill ? electricityBill.electricity_bill : 0,
                total_bill: 0, // Will be calculated below
                meter_reading: waterBill ? waterBill.meter_reading : 0,
                electricity_meter_reading: electricityBill ? electricityBill.electricity_meter_reading : 0,
                landlord_id: JSON.parse(originalText)[0][1],
            };

            // Calculate total bill - Use Wallet as arrears only if status is PENDING
            const rent = parseInt(tenant.rent.toString().replace(",", ""));
            const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;
            billEntry.total_bill = rent + arrears + parseFloat(billEntry.water_bill) + parseFloat(billEntry.electricity_bill);

            finalBill.push(billEntry);
        }

        // Validation: Ensure we have bills for all included tenants
        if (finalBill.length !== includedTenants.length) {
            showSnackbar({
                title: "Error!",
                message: "Failed to compile bills for all included tenants. Please check your inputs.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
            return;
        }

        if (!month) {
            showSnackbar({
                title: "Error!",
                message: "Kindly select the month you are billing for.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
            return;
        }

        // Calculate estimated total for the confirmation dialog
        const estimatedTotal = finalBill.reduce((sum, bill) => sum + (bill.total_bill || 0), 0);

        // Prepare billing data for confirmation dialog
        const billingConfirmData = {
            month: getMonthName(parseInt(month.getMonth())).toUpperCase(),
            year: parseInt(month.getFullYear()),
            propertyName: selectedProperty.property_name,
            tenantCount: finalBill.length,
            estimatedTotal: estimatedTotal
        };

        setBillingDataForConfirm({
            finalBill,
            billingConfirmData
        });

        setShowBillingConfirmDialog(true);
        setLoading(false);
    };

    // New function to handle confirmation
    const handleBillingConfirm = async () => {
        setShowBillingConfirmDialog(false);
        setLoading(true);

        const {finalBill} = billingDataForConfirm;

        // Make Billing API Call
        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/property/bill/tenants", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify([
                    ...finalBill,
                    {
                        month: parseInt(month.getMonth()),
                        year: parseInt(month.getFullYear()),
                        property_id: selectedProperty.id,
                        property_name: selectedProperty.property_name,
                    },
                ]),
            });

            // Handle authentication/authorization errors
            if (response.status === 401) {
                handle401Unauthorized();
                return;
            }
            if (response.status === 403) {
                handle403Forbidden();
                return;
            }
            if (response.status === 200) {
                const data = await response.json();
                setHtmlContent(data.htmlReport);

                showSnackbar({
                    title: "Success!",
                    message: "Tenants billed successfully.",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Failed Billing tenants.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            }
        } catch (err) {
            console.log(err);
            showSnackbar({
                title: "Error!",
                message: "Something Went Wrong. Call Customer Care For Assistance.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
        }
    };

    // New function to handle cancellation
    const handleBillingCancel = async () => {
        setShowBillingConfirmDialog(false);
        setLoading(false);
    };

    const downloadPDF = () => {
        fetch(ApiDetails + "pegasus/visionary/property/bill/report", {
            method: "POST",
            body: JSON.stringify({pdf: htmlContent}),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "File download will start shortly",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });

                    let blob = await response.blob();
                    let href = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", "billing_report.pdf");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                    onClose();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed downloading report",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while downloading report",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    // Helper function to get included tenants
    const getIncludedTenants = () => {
        return tenants.filter(tenant =>
            tenant.billing_enabled && tenantBillingStatus[tenant.tenant_id]
        );
    };

// Helper function to check if tenant has required readings
    const hasRequiredReadings = (tenant) => {
        const settings = tenantUtilitySettings[tenant.tenant_id] || {};

        if (settings.hasWater && !billedTenants.find(t => t.tenant_id === tenant.tenant_id)) {
            return false;
        }

        if (settings.hasElectricity && !elecBilledTenants.find(t => t.tenant_id === tenant.tenant_id)) {
            return false;
        }

        return true;
    };

// Helper function to get billing summary
    const getBillingSummary = () => {
        const includedTenants = getIncludedTenants();
        const tenantsWithWater = includedTenants.filter(t => tenantUtilitySettings[t.tenant_id]?.hasWater);
        const tenantsWithElectricity = includedTenants.filter(t => tenantUtilitySettings[t.tenant_id]?.hasElectricity);

        return {
            total: includedTenants.length,
            withWater: tenantsWithWater.length,
            withElectricity: tenantsWithElectricity.length,
            waterReadingsProvided: tenantsWithWater.filter(t => billedTenants.find(b => b.tenant_id === t.tenant_id)).length,
            electricityReadingsProvided: tenantsWithElectricity.filter(t => elecBilledTenants.find(b => b.tenant_id === t.tenant_id)).length
        };
    };

    return (
        <>
            {/*Standard Billing Dialog*/}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'visible'
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)})`,
                    position: 'relative'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    p: 1,
                                    borderRadius: 2,
                                    mr: 2,
                                    color: theme.palette.success.main
                                }}
                            >
                                <ReceiptLongIcon/>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Bill Property Tenants
                                </Typography>
                                {selectedProperty && (
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedProperty.property_name} • {selectedProperty.location}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{pt: 3}}>
                    {htmlContent !== "" ? (
                        <Box sx={{
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            padding: '24px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            width: '100%'
                        }}>
                            <div dangerouslySetInnerHTML={{__html: htmlContent}}/>
                        </Box>
                    ) : (
                        <Box>
                            {/* Compact Header Section */}
                            <Box sx={{
                                p: 2.5,
                                backgroundColor: "#f8fafc",
                                borderRadius: 2,
                                mb: 3,
                                border: "1px solid #e2e8f0"
                            }}>
                                <Grid container spacing={3} alignItems="center">
                                    {/* Billing Month Selection */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                                            <CalendarMonthIcon color="primary" sx={{fontSize: 20}}/>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                Billing Month
                                            </Typography>
                                        </Box>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                views={["year", "month"]}
                                                value={month}
                                                onChange={(newValue) => setMonth(newValue)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        label=""
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    {/* Billing Rates Info */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                                            <InfoIcon color="info" sx={{fontSize: 20}}/>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                Utility Rates
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                <WaterDropIcon sx={{fontSize: 16, color: "#2196F3"}}/>
                                                <Typography variant="caption">
                                                    <strong>{selectedProperty?.water_bill} KSH</strong>/unit
                                                    (min: {selectedProperty?.minimum_water_bill} KSH)
                                                </Typography>
                                            </Box>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                <ElectricBoltIcon sx={{fontSize: 16, color: "#FF9800"}}/>
                                                <Typography variant="caption">
                                                    <strong>{selectedProperty?.electricity_bill} KSH</strong>/unit
                                                    (min: {selectedProperty?.minimum_electricity_bill} KSH)
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Quick Actions */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                                            <SettingsIcon color="action" sx={{fontSize: 20}}/>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                Quick Actions
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PeopleIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkBillingToggle(false)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                Exclude All
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PeopleIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkBillingToggle(true)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                Include All
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<WaterDropIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkUtilityToggle('hasWater', false)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                No Water
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ElectricBoltIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkUtilityToggle('hasElectricity', false)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                No Electricity
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<RefreshIcon sx={{fontSize: 16}}/>}
                                                onClick={() => {
                                                    handleBulkUtilityToggle('hasWater', true);
                                                    handleBulkUtilityToggle('hasElectricity', true);
                                                }}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                Reset Utilities
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Tenants DataGrid */}
                            <Box sx={{width: '100%', position: 'relative'}}>
                                {fetchingTenants ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <CircularProgress
                                            size={60}
                                            thickness={4}
                                            sx={{
                                                color: theme.palette.primary.main,
                                                mb: 2
                                            }}
                                        />
                                        <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                                            Loading Tenants
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Fetching tenant data for {selectedProperty?.property_name}...
                                        </Typography>
                                    </Box>
                                ) : tenants.length === 0 ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <PeopleAltIcon
                                            sx={{
                                                fontSize: 60,
                                                color: theme.palette.text.disabled,
                                                mb: 2
                                            }}
                                        />
                                        <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                                            No Tenants Found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            There are no tenants in this property yet.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <StyledDataGrid
                                        rows={tenants}
                                        columns={billingColumns}
                                        autoHeight
                                        disableSelectionOnClick
                                        initialState={{
                                            sorting: {sortModel: [{field: "house_number", sort: "asc"}]},
                                            pagination: {paginationModel: {pageSize: 10}}
                                        }}
                                        rowsPerPageOptions={[10]}
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{p: 3, pt: 1}}>
                    {htmlContent !== "" ? (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<SendIcon/>}
                                onClick={downloadPDF}
                                sx={{
                                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                }}
                            >
                                Download PDF
                            </Button>
                            <Button onClick={() => window.location.reload()} color="inherit">
                                Close
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={onClose} color="inherit">
                                Cancel
                            </Button>
                            <LoadingButton
                                onClick={handleBillTenants}
                                loading={loading}
                                disabled={fetchingTenants || tenants.length === 0}
                                variant="contained"
                                startIcon={<SendIcon/>}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                }}
                            >
                                Send Bills
                            </LoadingButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <CardBillingConfirmationDialog
                isOpen={showBillingConfirmDialog}
                onConfirm={handleBillingConfirm}
                onCancel={handleBillingCancel}
                billingData={billingDataForConfirm?.billingConfirmData || {}}
            />
        </>
    );
};

// Property metrics card component
const PropertyMetricsCard = ({property, metrics, properties, onPropertyChange}) => {
    const theme = useTheme();
    const navigate = useNavigate();

    // Format currency for better readability
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).format(value);
    };

    // Format numbers with one decimal place
    const formatNumber = (value) => {
        return value.toFixed(1);
    };

    // Handle navigation to property details
    const handleNavigateToProperty = () => {
        if (property?.id) {
            navigate(`/property/${property.id}`);
        }
    };

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'visible'
            }}
        >
            <CardContent sx={{p: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
                    <Box
                        sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            p: 1.5,
                            borderRadius: 2,
                            mr: 2,
                            color: theme.palette.primary.main
                        }}
                    >
                        <HomeWorkIcon fontSize="large"/>
                    </Box>
                    <FormControl fullWidth variant="standard" sx={{minWidth: 120}}>
                        <Select
                            value={property?.id || ''}
                            onChange={onPropertyChange}
                            IconComponent={KeyboardArrowDownIcon}
                            displayEmpty
                            renderValue={() => (
                                <Box
                                    onClick={handleNavigateToProperty}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8
                                        }
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold" sx={{mb: 0.5}}>
                                        {property.property_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {property.location}
                                    </Typography>
                                </Box>
                            )}
                            sx={{
                                '.MuiSelect-select': {
                                    paddingY: 0,
                                    paddingX: 0,
                                    border: 'none'
                                },
                                '&:before, &:after': {
                                    display: 'none'
                                }
                            }}
                        >
                            {properties.map((prop) => (
                                <MenuItem key={prop.id} value={prop.id}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="medium">
                                            {prop.property_name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {prop.location}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{my: 2}}/>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Income Due
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium">
                                {formatCurrency(metrics.expectedIncome)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Income Collected
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium" color="success.main">
                                {formatCurrency(metrics.receivedIncome)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6}>
                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Expenses
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium" color="error.main">
                                {formatCurrency(metrics.expenseCost)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6}>
                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Total Refundable Deposit
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium">
                                {formatCurrency(metrics.depositHeld)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const EnhancedProgressBars = () => {
    const theme = useTheme();

    // Safe decryption function
    const safeDecrypt = (key) => {
        try {
            return CryptoJS.AES.decrypt(localStorage.getItem(key), KEY).toString(CryptoJS.enc.Utf8) || "";
        } catch {
            return "";
        }
    };

    // Safe decryption function
    const safeDecryptMyMap = (key) => {
        try {
            return CryptoJS.AES.decrypt(localStorage.getItem(key), KEY).toString(CryptoJS.enc.Utf8) || "";
        } catch {
            return "";
        }
    };

    const originalText = safeDecryptMyMap("myMap");

    // Retrieve decrypted data from localStorage
    const [originalText2, setOriginalText2] = useState(safeDecrypt("portfolioDetails"));
    const [parsedData, setParsedData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [addTenantOpen, setAddTenantOpen] = useState(false);
    const [billTenantsOpen, setBillTenantsOpen] = useState(false);
    const [metrics, setMetrics] = useState({
        totalUnits: 0,
        occupiedUnits: 0,
        vacantUnits: 0,
        expectedIncome: 0,
        receivedIncome: 0,
        tax: 0,
        depositHeld: 0,
        expenseCost: 0,
        profit: 0,
        incomePercentage: 0,
        occupancyPercentage: 0,
        profitPercentage: 0,
    });

    // Add these state variables at the top of your component with other useState declarations
    const [reportLoading, setReportLoading] = useState(false);
    const [reportType, setReportType] = useState('PENDING'); // Since this is for pending bills

// Enhanced handleGenerateReport function
    const handleGenerateReport = () => {
        setReportLoading(true); // Show the loading backdrop
        setReportType('PENDING'); // Set report type for the loading UI
        /*

                if (true) {
                    console.log("Access Token");
                    console.log(JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1])
                    return;
                }
        */

        // Validation
        if (!selectedProperty || !selectedProperty.id) {
            setReportLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please select a property first",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Prepare submission data
        const submitData = {
            property_id: selectedProperty.id
        };

        fetch(ApiDetails + "pegasus/visionary/reports/get/property/tenant/pending-bills-report", {
            method: "POST",
            body: JSON.stringify(submitData),
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }

                if (response.status === 200) {
                    // Handle file download
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Pending_Bills_Trend_Report_${selectedProperty.property_name}_${new Date().toISOString().split('T')[0]}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    showSnackbar({
                        title: "Success!",
                        message: "Report generated successfully. Download will start shortly",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to generate report. Please try again later",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setReportLoading(false); // Hide the loading backdrop
            })
            .catch((err) => {
                setReportLoading(false); // Hide the loading backdrop
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

// Handle cancel report function for the loading UI
    const handleCancelReport = () => {
        setReportLoading(false);
        showSnackbar({
            title: "Cancelled",
            message: "Report generation cancelled",
            type: "info",
            position: {vertical: "bottom", horizontal: "right"},
        });
    };


    // Format the data
    useEffect(() => {
        try {
            if (originalText2) {
                const parsed = JSON.parse(originalText2);
                if (!parsed || !Array.isArray(parsed) || !parsed[1]?.[1]) {
                    throw new Error("Invalid portfolio details structure");
                }
                setParsedData(parsed);
                const propertiesList = parsed[1][1] || [];
                setProperties(propertiesList);
                if (propertiesList.length > 0) {
                    setSelectedProperty(propertiesList[0]);
                } else {
                    showSnackbar({
                        title: "Warning!",
                        message: "No properties found. Please add a property to continue.",
                        type: "warning",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Portfolio details not found. Please log in again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
            setLoading(false);
        } catch (error) {
            console.error("Error parsing data:", error);
            showSnackbar({
                title: "Error!",
                message: "Failed to load portfolio details. Please try again or contact support.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
        }
    }, [originalText2]);
    // Calculate metrics when selected property changes
    useEffect(() => {
        if (!parsedData || !selectedProperty) return;

        // Extract the maps
        const expectedIncomeMap = parsedData[8][1] || {};
        const receivedIncomeMap = parsedData[9][1] || {};
        const taxMap = parsedData[16][1] || {};
        const depositHeldMap = parsedData[17][1] || {};
        const expenseCostMap = parsedData[19][1] || {};
        const occupancyMap = parsedData[20][1] || {};
        const occupiedUnitsMap = parsedData[21][1] || {};
        const vacantUnitsMap = parsedData[22][1] || {};

        const propertyId = selectedProperty.id;

        // Get the metrics for the selected property
        const vacantUnits = vacantUnitsMap[propertyId] || 0;
        const occupiedUnits = occupiedUnitsMap[propertyId] || 0;
        const totalUnits = vacantUnits + occupiedUnits;
        const expectedIncome = expectedIncomeMap[propertyId] || 0;
        const receivedIncome = receivedIncomeMap[propertyId] || 0;
        const tax = taxMap[propertyId] || 0;
        const depositHeld = depositHeldMap[propertyId] || 0;
        const expenseCost = expenseCostMap[propertyId] || 0;
        const profit = receivedIncome - expenseCost;
        const occupancy = occupancyMap[propertyId] || 0;

        // Calculate percentages
        const incomePercentage = Math.round((receivedIncome / (expectedIncome || 1)) * 100);
        const profitPercentage = Math.round((profit / (receivedIncome || 1)) * 100);

        // Progressive animation for the metrics
        setMetrics({
            totalUnits,
            occupiedUnits,
            vacantUnits,
            expectedIncome,
            receivedIncome,
            tax,
            depositHeld,
            expenseCost,
            profit,
            incomePercentage: 0,
            occupancyPercentage: 0,
            profitPercentage: 0,
        });

        // Animate the progress bars
        const timer1 = setTimeout(() => {
            setMetrics(prev => ({...prev, incomePercentage}));
        }, 300);

        const timer2 = setTimeout(() => {
            setMetrics(prev => ({...prev, occupancyPercentage: occupancy}));
        }, 600);

        const timer3 = setTimeout(() => {
            setMetrics(prev => ({...prev, profitPercentage}));
        }, 900);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [selectedProperty, parsedData]);

    // Handle property change
    const handlePropertyChange = (event) => {
        const propertyId = parseInt(event.target.value, 10);
        const property = properties.find((prop) => prop.id === propertyId);
        if (property) {
            setSelectedProperty(property);
        }
    };

// Format number with one decimal place
    const formatNumberWithDecimals = (value) => {
        return Number(value).toFixed(1);
    };

// Action handlers
    const handleAddTenant = () => {
        setAddTenantOpen(true);
    };

    const handleBillTenants = () => {
        if (!selectedProperty || !selectedProperty.id) {
            showSnackbar({
                title: "Error!",
                message: "No property selected. Please select a property to bill tenants.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }
        setBillTenantsOpen(true);
    };

    return (
        <Box sx={{pb: 2}}>

            <Grid container spacing={3}>

                {/* Right Side - Property Details */}
                <Grid item xs={12} lg={4}>
                    {selectedProperty && (
                        <PropertyMetricsCard
                            property={selectedProperty}
                            metrics={metrics}
                            properties={properties}
                            onPropertyChange={handlePropertyChange}
                        />
                    )}
                </Grid>

                {/* Left Side - Progress Metrics and Unit Stats */}
                <Grid item xs={12} lg={8}>
                    <Grid container spacing={3}>
                        {/* Performance Metrics */}
                        <Grid item xs={12}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <Typography variant="h6" fontWeight="bold">
                                        Performance Metrics
                                    </Typography>

                                    {/* Integrated Quick Actions */}
                                    {selectedProperty && (
                                        <Box sx={{display: 'flex', gap: 1}}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<PersonAddIcon/>}
                                                onClick={handleAddTenant}
                                                size="small"
                                                sx={{
                                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                                    color: theme.palette.primary.main,
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                                    }
                                                }}
                                            >
                                                Add Tenant
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={<ReceiptLongIcon/>}
                                                onClick={handleBillTenants}
                                                size="small"
                                                disabled={!selectedProperty || !selectedProperty.id}
                                                sx={{
                                                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                                    boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`,
                                                    '&:hover': {
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`
                                                    }
                                                }}
                                            >
                                                Bill Tenants
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<AssessmentIcon/>}
                                                onClick={handleGenerateReport}
                                                size="small"
                                                disabled={!selectedProperty || !selectedProperty.id}
                                                sx={{
                                                    borderColor: alpha(theme.palette.warning.main, 0.3),
                                                    color: theme.palette.warning.main,
                                                    '&:hover': {
                                                        borderColor: theme.palette.warning.main,
                                                        backgroundColor: alpha(theme.palette.warning.main, 0.08)
                                                    }
                                                }}
                                            >
                                                Trend Report
                                            </Button>
                                        </Box>
                                    )}
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <ProgressCircle
                                            value={metrics.incomePercentage < 0 ? 0 : metrics.incomePercentage}
                                            text={`${formatNumberWithDecimals(metrics.incomePercentage)}%`}
                                            label="Collection Rate"
                                            // description={`KSH ${formatNumberWithDecimals(metrics.receivedIncome)} of ${formatNumberWithDecimals(metrics.expectedIncome)}`}
                                            description={``}
                                            icon={<AttachMoneyIcon/>}
                                            color={theme.palette.success.main}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <ProgressCircle
                                            value={metrics.occupancyPercentage}
                                            text={`${formatNumberWithDecimals(metrics.occupancyPercentage)}%`}
                                            label="Occupancy Rate"
                                            // description={`${formatNumberWithDecimals(metrics.occupiedUnits)} of ${formatNumberWithDecimals(metrics.totalUnits)} units occupied`}
                                            description={``}
                                            icon={<PeopleAltIcon/>}
                                            color={theme.palette.info.main}
                                        />
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        {/* Unit Statistics */}
                        <Grid item xs={12}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" sx={{mb: 3}}>
                                    Unit Statistics
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <MeetingRoomIcon
                                                    sx={{color: theme.palette.primary.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Units
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold">
                                                {formatNumberWithDecimals(metrics.totalUnits)}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <PeopleAltIcon
                                                    sx={{color: theme.palette.success.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Occupied
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" color="success.main">
                                                {formatNumberWithDecimals(metrics.occupiedUnits)}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <MeetingRoomIcon
                                                    sx={{color: theme.palette.error.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Vacant
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" color="error.main">
                                                {formatNumberWithDecimals(metrics.vacantUnits)}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <PercentIcon
                                                    sx={{color: theme.palette.info.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Occupancy
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" color="info.main">
                                                {formatNumberWithDecimals(metrics.occupancyPercentage)}%
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Dialogs */}
            <AddTenantDialog
                open={addTenantOpen}
                onClose={() => setAddTenantOpen(false)}
                selectedProperty={selectedProperty}
            />

            <BillTenantsDialog
                open={billTenantsOpen}
                onClose={() => setBillTenantsOpen(false)}
                selectedProperty={selectedProperty}
            />

            {/* Report Loading UI */}
            <ReportLoadingBackdrop
                open={reportLoading}
                sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(4px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 1300
                }}
                aria-label="Report generation in progress"
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        padding: 3,
                        position: 'relative'
                    }}
                >
                    {/* Main Loading Card */}
                    <Card
                        elevation={24}
                        sx={{
                            borderRadius: 3,
                            padding: 4,
                            minWidth: 320,
                            maxWidth: 480,
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 20px rgba(0, 0, 0, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.8)',
                            position: 'relative',
                            overflow: 'visible',
                            transform: 'translateY(0)',
                            animation: 'slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            '@keyframes slideInUp': {
                                '0%': {
                                    transform: 'translateY(40px)',
                                    opacity: 0
                                },
                                '100%': {
                                    transform: 'translateY(0)',
                                    opacity: 1
                                }
                            }
                        }}
                    >
                        {/* Progress Indicator */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mb: 3,
                                position: 'relative'
                            }}
                        >
                            {/* Outer Ring */}
                            <CircularProgress
                                size={80}
                                thickness={2}
                                sx={{
                                    color: 'rgba(46, 125, 50, 0.2)',
                                    position: 'absolute'
                                }}
                                variant="determinate"
                                value={100}
                            />

                            {/* Main Progress */}
                            <CircularProgress
                                size={80}
                                thickness={4}
                                sx={{
                                    color: '#2e7d32',
                                    filter: 'drop-shadow(0 2px 4px rgba(46, 125, 50, 0.3))',
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    }
                                }}
                            />

                            {/* Center Icon */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'rgba(46, 125, 50, 0.1)',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    animation: 'pulse 2s infinite ease-in-out',
                                    '@keyframes pulse': {
                                        '0%, 100%': {
                                            transform: 'translate(-50%, -50%) scale(1)',
                                            opacity: 1
                                        },
                                        '50%': {
                                            transform: 'translate(-50%, -50%) scale(1.1)',
                                            opacity: 0.8
                                        }
                                    }
                                }}
                            >
                                <DescriptionIcon
                                    sx={{
                                        color: '#2e7d32',
                                        fontSize: 20
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Main Content */}
                        <Box sx={{textAlign: 'center', mb: 3}}>
                            <Typography
                                variant="h5"
                                fontWeight="600"
                                sx={{
                                    mb: 1,
                                    color: '#1a1a1a',
                                    letterSpacing: '-0.5px'
                                }}
                            >
                                Generating Report
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#666',
                                    mb: 2,
                                    lineHeight: 1.6
                                }}
                            >
                                Creating your {reportType === 'PAID' ? 'Paid' : 'Pending'} tenants report
                            </Typography>

                            {/* Progress Steps */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 1,
                                mb: 3
                            }}>
                                {[
                                    'Fetching data',
                                    'Processing records',
                                    'Formatting report',
                                    'Finalizing'
                                ].map((step, index) => (
                                    <Box
                                        key={step}
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: '#2e7d32',
                                            opacity: 0.3,
                                            animation: `stepPulse 2s infinite ${index * 0.5}s`,
                                            '@keyframes stepPulse': {
                                                '0%, 80%, 100%': {
                                                    opacity: 0.3,
                                                    transform: 'scale(1)'
                                                },
                                                '40%': {
                                                    opacity: 1,
                                                    transform: 'scale(1.2)'
                                                }
                                            }
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Estimated Time */}
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#999',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    mb: 1
                                }}
                            >
                                This usually takes 30-60 seconds
                            </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            pt: 2,
                            borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                        }}>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon/>}
                                onClick={handleCancelReport}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#666',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: '#d32f2f',
                                        color: '#d32f2f',
                                        backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.15)'
                                    }
                                }}
                                aria-label="Cancel report generation"
                            >
                                Send to background
                            </Button>
                        </Box>
                    </Card>

                    {/* Background Elements for Visual Interest */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '20%',
                            left: '10%',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            background: 'linear-gradient(45deg, rgba(46, 125, 50, 0.1), rgba(46, 125, 50, 0.05))',
                            animation: 'float 6s ease-in-out infinite',
                            '@keyframes float': {
                                '0%, 100%': {transform: 'translateY(0px)'},
                                '50%': {transform: 'translateY(-20px)'}
                            }
                        }}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '20%',
                            right: '15%',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
                            animation: 'float 8s ease-in-out infinite reverse',
                        }}
                    />
                </Box>
            </ReportLoadingBackdrop>
        </Box>
    );
};

export default EnhancedProgressBars;